/* ═══════════════════════════════════════════════════════════════
   Page breaks — what the Pages view draws between two pages.

   [blank paper down to the footer] [footer, page N] [page gap]
   [header, page N+1]. The bands resolve `{Page}` / `{NumPages}` /
   `{PageOfPages}` and the forma's first / odd / even pages exactly as
   everywhere else. With zone wiring (a forma editor) each band is the SAME
   live object the sheet's own bands are (ZoneShell + ZoneBandEdit), wearing
   THAT page's variant — so a band clicked, dropped on or right-clicked in
   the middle of the document edits that page's band. Without wiring (a
   read-only paper) they are the display ZoneBand.

   Two hosts:
   - The Designer's live document: ProseMirror WIDGET decorations (never a
     document change — undo, export and co-editing never see them). The
     widget's skeleton is plain DOM; the bands are React portals into it,
     so they follow the forma and the page count without rebuilding.
   - Forma Studio's sample body: plain React elements (`PageBreakElement`).

   The plugin is registered only while Pages is on, appended after every
   other plugin, so the collaboration cursors keep drawing as before.

   Hand-built by Josh's express approval (2026-11-02) — see paginate.ts.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import type { StandardConfig } from '../../../shared/types';
import { ZoneBand, type PaperContext, type ZoneKind } from './ZoneBand';
import { ZoneBandEdit } from './ZoneBandEdit';
import { ZoneShell } from './ZoneShell';
import type { DesignWiring } from '../designWiring';
import { bandIsEmpty, bandVariantName, positionalVariant } from '../formaZonesModel';
import { resolvePageGeometry } from '../../../shared/body-layout';
import { BlankPagePreview } from '../BlankPagePreview';
import { classifyBlocks, type PageBreak } from './paginate';
import { PAGE_BREAK_CLASS, measureFlow, type Pagination, type PagerSurface } from './usePagination';

// ─── The bands inside one break ───

/** One band of a break: the footer of the page ending here or the header
 *  of the page starting here. */
function BreakBand({
  kind,
  paper,
  page,
  numPages,
  design,
}: {
  kind: Extract<ZoneKind, 'header' | 'footer'>;
  paper: PaperContext;
  page: number;
  numPages: number;
  design?: DesignWiring;
}) {
  if (!design) return <ZoneBand kind={kind} pageNum={page} numPages={numPages} {...paper} />;
  const variant = positionalVariant(page, resolvePageGeometry(paper.forma));
  return (
    <ZoneShell
      band={kind}
      design={design}
      variant={variant}
      empty={bandIsEmpty(paper.forma, kind, variant)}
    >
      <ZoneBandEdit
        kind={kind}
        pageNum={page}
        numPages={numPages}
        variant={bandVariantName(variant)}
        edit={design.edit}
        onRequestTextEdit={design.onRequestTextEdit}
        onZoneInsertAt={design.onZoneInsertAt}
        onZoneReplaceAt={design.onZoneReplaceAt}
        onNotice={design.onNotice}
        {...paper}
      />
    </ZoneShell>
  );
}

/** A break as a React element (Forma Studio's sample body). `endsPage` is
 *  the page that ends here; the next page starts right after. */
export function PageBreakElement({
  paper,
  endsPage,
  numPages,
  design,
}: {
  paper: PaperContext;
  endsPage: number;
  numPages: number;
  /** Zone wiring → the bands are live (see the file header). */
  design?: DesignWiring;
}) {
  return (
    <div
      className={PAGE_BREAK_CLASS}
      data-pb-page={endsPage}
      aria-hidden={design ? undefined : 'true'}
      // A click on a break is a click on the margin — never a sample row.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dsg-pb-fill" />
      <div className="dsg-pb-foot">
        <BreakBand
          kind="footer"
          paper={paper}
          page={endsPage}
          numPages={numPages}
          design={design}
        />
      </div>
      <div className="dsg-pb-gap" />
      <div className="dsg-pb-head">
        <BreakBand
          kind="header"
          paper={paper}
          page={endsPage + 1}
          numPages={numPages}
          design={design}
        />
      </div>
    </div>
  );
}

/** The intentionally-blank page as the last page of the stack. */
export function PagedBlankPage({ forma }: { forma: StandardConfig }) {
  return (
    <>
      <div className="dsg-pb-gap" aria-hidden="true" />
      <div className="dsg-pb-blank">
        <BlankPagePreview forma={forma} />
      </div>
    </>
  );
}

// ─── ProseMirror host ───

interface BreakHost {
  el: HTMLElement;
  endsPage: number;
}

/** The live widget skeletons, for the React portals. */
export interface PageBreakRegistry {
  subscribe: (cb: () => void) => () => void;
  snapshot: () => BreakHost[];
  add: (h: BreakHost) => void;
  remove: (el: Node) => void;
}

export function createPageBreakRegistry(): PageBreakRegistry {
  let hosts: BreakHost[] = [];
  const subs = new Set<() => void>();
  let queued = false;
  const notify = () => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      subs.forEach((cb) => cb());
    });
  };
  return {
    subscribe: (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    snapshot: () => hosts,
    add: (h) => {
      hosts = [...hosts, h];
      notify();
    },
    remove: (el) => {
      const next = hosts.filter((h) => h.el !== el);
      if (next.length !== hosts.length) {
        hosts = next;
        notify();
      }
    },
  };
}

export const pageBreaksKey = new PluginKey<DecorationSet>('dsgPageBreaks');

interface PageBreaksMeta {
  breaks: Array<{ pos: number; endsPage: number }>;
  overflows: number[];
}

function skeleton(registry: PageBreakRegistry, endsPage: number): HTMLElement {
  const el = document.createElement('div');
  el.className = PAGE_BREAK_CLASS;
  el.contentEditable = 'false';
  el.setAttribute('aria-hidden', 'true');
  el.dataset.pbPage = String(endsPage);
  for (const c of ['dsg-pb-fill', 'dsg-pb-foot', 'dsg-pb-gap', 'dsg-pb-head']) {
    const d = document.createElement('div');
    d.className = c;
    el.appendChild(d);
  }
  registry.add({ el, endsPage });
  return el;
}

function buildSet(doc: PMNode, meta: PageBreaksMeta, registry: PageBreakRegistry) {
  const decos: Decoration[] = [];
  const max = doc.content.size;
  for (const b of meta.breaks) {
    const pos = Math.max(0, Math.min(max, b.pos));
    const inline = doc.resolve(pos).depth > 0;
    decos.push(
      Decoration.widget(pos, () => skeleton(registry, b.endsPage), {
        // Between blocks: before every other widget at that spot (module
        // tags, table numbers ride with the block on the next page).
        // Inside a paragraph: before the caret at that line start.
        side: inline ? -1 : -10,
        ignoreSelection: true,
        stopEvent: () => true,
        key: `dsg-pb-${b.endsPage}-${inline ? 'i' : 'b'}`,
        destroy: (node) => registry.remove(node),
      })
    );
  }
  doc.forEach((node, offset, index) => {
    if (meta.overflows.includes(index)) {
      decos.push(
        Decoration.node(offset, offset + node.nodeSize, {
          class: 'dsg-page-overflow',
          title: 'Taller than a page — it runs past the page edge here',
        })
      );
    }
  });
  return DecorationSet.create(doc, decos);
}

export function pageBreaksPlugin(registry: PageBreakRegistry): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: pageBreaksKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, set) {
        const meta = tr.getMeta(pageBreaksKey) as PageBreaksMeta | undefined;
        if (meta) return buildSet(tr.doc, meta, registry);
        return tr.docChanged ? set.map(tr.mapping, tr.doc) : set;
      },
    },
    props: {
      decorations: (state) => pageBreaksKey.getState(state),
    },
  });
}

/** Hand the plugin a new layout. Meta only — no step, never in undo. */
export function setPageBreaks(tr: Transaction, pages: Pagination): Transaction {
  const breaks: PageBreaksMeta['breaks'] = [];
  pages.breaks.forEach((b, i) => {
    const pos = pages.anchors[i];
    if (pos != null) breaks.push({ pos, endsPage: b.pageNum - 1 });
  });
  return tr
    .setMeta(pageBreaksKey, { breaks, overflows: pages.overflows } satisfies PageBreaksMeta)
    .setMeta('addToHistory', false);
}

/** The bands of every live widget, portalled into its skeleton. */
export function PageBreakPortals({
  registry,
  paper,
  numPages,
  design,
}: {
  registry: PageBreakRegistry;
  paper: PaperContext;
  numPages: number;
  /** Zone wiring → the bands are live (see the file header). */
  design?: DesignWiring;
}) {
  const hosts = React.useSyncExternalStore(registry.subscribe, registry.snapshot);
  // Live bands are real controls: the skeleton stops hiding them from
  // assistive tech. (ProseMirror ignores attribute changes inside a widget.)
  const live = !!design;
  React.useLayoutEffect(() => {
    hosts.forEach((h) => {
      if (live) h.el.removeAttribute('aria-hidden');
      else h.el.setAttribute('aria-hidden', 'true');
    });
  }, [hosts, live]);
  return (
    <>
      {hosts.map((h, i) => {
        const foot = h.el.querySelector('.dsg-pb-foot');
        const head = h.el.querySelector('.dsg-pb-head');
        if (!foot || !head) return null;
        return (
          <React.Fragment key={i}>
            {createPortal(
              <BreakBand
                kind="footer"
                paper={paper}
                page={h.endsPage}
                numPages={numPages}
                design={design}
              />,
              foot
            )}
            {createPortal(
              <BreakBand
                kind="header"
                paper={paper}
                page={h.endsPage + 1}
                numPages={numPages}
                design={design}
              />,
              head
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

/** The ProseMirror position where a line inside a paragraph starts, for a
 *  split `offset` (natural px from the block's top). Null → not found. */
function lineStartPos(
  view: EditorView,
  el: HTMLElement,
  start: number,
  node: PMNode,
  offset: number,
  lineHeight: number,
  scale: number
): number | null {
  const r = el.getBoundingClientRect();
  // Breaks already drawn inside this paragraph push the later lines down.
  let shift = 0;
  el.querySelectorAll<HTMLElement>(`.${PAGE_BREAK_CLASS}`).forEach((w) => {
    const wr = w.getBoundingClientRect();
    if ((wr.top - r.top) / scale - shift <= offset + 0.5) shift += wr.height / scale;
  });
  const cs = getComputedStyle(el);
  const y = r.top + (offset + shift + lineHeight / 2) * scale;
  const x = r.left + ((parseFloat(cs.paddingLeft) || 0) + 2) * scale;
  const hit = view.posAtCoords({ left: x, top: y });
  if (!hit) return null;
  const first = start + 1;
  const last = start + node.nodeSize - 1;
  let pos = Math.max(first, Math.min(last, hit.pos));
  // Walk back to the first character of that visual line.
  const lineTop = view.coordsAtPos(pos, 1).top;
  const tol = (lineHeight * scale) / 2;
  for (let k = 0; k < 400 && pos - 1 > first; k++) {
    if (Math.abs(view.coordsAtPos(pos - 1, 1).top - lineTop) > tol) break;
    pos--;
  }
  return pos > first ? pos : null;
}

/** The Designer's document as a paged surface. */
export function proseMirrorPagerSurface(
  editor: Editor,
  getForma: () => StandardConfig | null
): PagerSurface {
  return {
    collect: ({ sheet, scale }) => {
      if (editor.isDestroyed) return null;
      const view = editor.view;
      if (!view.dom.isConnected) return null;
      const doc = view.state.doc;
      const levels = (getForma()?.levels || []) as Array<{ keepLinesTogether?: boolean }>;
      const els: Array<HTMLElement | null> = [];
      const starts: number[] = [];
      const nodes: PMNode[] = [];
      const items: Parameters<typeof classifyBlocks>[0] = [];
      doc.forEach((node, offset) => {
        const dom = view.nodeDOM(offset);
        els.push(dom instanceof HTMLElement ? dom : null);
        starts.push(offset);
        nodes.push(node);
        const level = Number(node.attrs.level) || 1;
        items.push(
          node.type.spec.tableRole === 'table'
            ? { type: 'table' }
            : node.isTextblock
              ? { type: 'text', level, keepLines: !!levels[level - 1]?.keepLinesTogether }
              : { type: 'other' }
        );
      });
      const blocks = measureFlow(sheet, view.dom, els, classifyBlocks(items), scale);
      return {
        blocks,
        anchorOf: (b: PageBreak) => {
          const i = b.beforeBlockIndex;
          if (i >= starts.length) return doc.content.size;
          if (b.splitOffset == null) return starts[i];
          const el = els[i];
          const lh = blocks[i]?.lineHeight;
          if (!el || !lh) return starts[i];
          return lineStartPos(view, el, starts[i], nodes[i], b.splitOffset, lh, scale) ?? starts[i];
        },
      };
    },
    subscribe: (onChange) => {
      const fn = ({ transaction }: { transaction: Transaction }) => {
        if (transaction.getMeta(pageBreaksKey)) return;
        // A selection move changes no layout; a document change or a
        // meta-only restyle (levels, marks, review) might.
        if (transaction.docChanged || !transaction.selectionSet) onChange();
      };
      editor.on('transaction', fn);
      return () => {
        editor.off('transaction', fn);
      };
    },
  };
}

/** Forma Studio's sample body as a paged surface: `.dsg-block` rows. */
export function sampleBodyPagerSurface(getFlow: () => HTMLElement | null): PagerSurface {
  return {
    collect: ({ sheet, scale }) => {
      const flow = getFlow();
      if (!flow || !flow.isConnected) return null;
      const els = Array.from(flow.children).filter(
        (c): c is HTMLElement => c instanceof HTMLElement && c.classList.contains('dsg-block')
      );
      const kinds = classifyBlocks(
        els.map((el) => ({ type: 'text' as const, level: levelOfRow(el) }))
      );
      // The sample's rows are unsplittable: a break draws between rows.
      return { blocks: measureFlow(sheet, flow, els, kinds, scale).map(noLines) };
    },
  };
}

/** A sample row's level, from its `dsg-level-N` class. */
function levelOfRow(el: HTMLElement): number {
  for (const c of Array.from(el.classList)) {
    if (c.startsWith('dsg-level-')) return Number(c.slice('dsg-level-'.length)) || 1;
  }
  return 1;
}

function noLines<T extends { lineHeight?: number }>(b: T): T {
  if (b.lineHeight == null) return b;
  const { lineHeight: _drop, ...rest } = b;
  void _drop;
  return rest as T;
}
